import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTopics, getClusteringStatus, regenerateTopics } from '../services/topics';
import { setTitle } from '../utils/environment';

const Topics = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateMessage, setRegenerateMessage] = useState(null);
  const pollingRef = useRef(null);

  const fetchTopics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTopics({
        limit: 50,
        unread_only: true
      });
      setTopics(response.topics || []);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check clustering status and start polling if running
  const checkClusteringStatus = useCallback(async () => {
    try {
      const status = await getClusteringStatus();
      setIsRegenerating(status.is_running);

      if (status.is_running && !pollingRef.current) {
        // Start polling
        pollingRef.current = setInterval(async () => {
          try {
            const newStatus = await getClusteringStatus();
            if (!newStatus.is_running) {
              // Clustering finished, stop polling and refresh
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setIsRegenerating(false);
              setRegenerateMessage('Topics regenerated successfully!');
              setTimeout(() => setRegenerateMessage(null), 3000);
              await fetchTopics();
            }
          } catch (pollErr) {
            console.error('Error polling clustering status:', pollErr);
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Error checking clustering status:', err);
    }
  }, [fetchTopics]);

  // Handle regeneration request
  const handleRegenerate = async () => {
    setShowConfirmDialog(false);
    try {
      const response = await regenerateTopics();
      if (response.success) {
        setIsRegenerating(true);
        setRegenerateMessage('Regenerating topics...');
        // Start polling for completion
        checkClusteringStatus();
      } else {
        setRegenerateMessage(response.error || 'Failed to start regeneration');
        setTimeout(() => setRegenerateMessage(null), 5000);
      }
    } catch (err) {
      console.error('Failed to regenerate topics:', err);
      setRegenerateMessage(err.message);
      setTimeout(() => setRegenerateMessage(null), 5000);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);


  const handleTopicClick = (topicId) => {
    navigate(`/topics/${topicId}`);
  };

  const formatScore = (score) => {
    return Math.round(score * 100);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-emerald-700 bg-emerald-100 border-fun-mint';
    if (score >= 0.6) return 'text-primary-700 bg-primary-100 border-primary-400';
    if (score >= 0.4) return 'text-amber-700 bg-amber-100 border-fun-lemon';
    return 'text-gray-600 bg-gray-100 border-gray-300';
  };

  const formatRecency = (days) => {
    if (days < 1) return 'today';
    if (days < 2) return '1 day ago';
    if (days < 7) return `${Math.round(days)} days ago`;
    if (days < 30) return `${Math.round(days / 7)} weeks ago`;
    return `${Math.round(days / 30)} months ago`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  useEffect(() => {
    setTitle('Topics');
    fetchTopics();
    checkClusteringStatus();
  }, [fetchTopics, checkClusteringStatus]);

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="overlay-clay flex items-center justify-center">
          <div className="modal-clay p-6 max-w-md mx-4">
            <div className="text-center mb-4">
              <span className="text-4xl" role="img" aria-hidden="true">🔄</span>
            </div>
            <h3 className="font-display text-lg font-bold text-gray-900 mb-2 text-center">Regenerate Topics?</h3>
            <p className="text-gray-600 mb-6 text-center">
              This will delete all existing topics and create new ones based on your current articles.
              This process may take a minute or two.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="btn-clay-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                className="btn-clay-accent"
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Message */}
      {regenerateMessage && (
        <div className={`fixed top-4 right-4 z-40 px-4 py-3 rounded-clay border-3 shadow-clay ${
          regenerateMessage.includes('success') ? 'bg-emerald-50 text-emerald-800 border-fun-mint' :
          regenerateMessage.includes('Regenerating') ? 'bg-primary-50 text-primary-800 border-primary-400' :
          'bg-rose-50 text-rose-800 border-fun-coral'
        }`}>
          <div className="flex items-center">
            {isRegenerating && (
              <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
            )}
            <span className="font-medium">{regenerateMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b-3 border-clay-border shadow-clay-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="btn-clay-ghost mr-4 p-2"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <span role="img" aria-hidden="true">📊</span>
                  Topics
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {topics.length} AI-generated topic clusters
                </p>
              </div>
            </div>
            {/* Subtle regenerate button */}
            <button
              onClick={() => setShowConfirmDialog(true)}
              disabled={isRegenerating}
              className={`p-2.5 rounded-clay border-2 transition-all ${
                isRegenerating
                  ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                  : 'text-gray-400 border-gray-200 hover:text-primary-600 hover:border-primary-400 hover:bg-primary-50'
              }`}
              title={isRegenerating ? 'Regenerating...' : 'Regenerate topics'}
            >
              <svg
                className={`w-5 h-5 ${isRegenerating ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border-3 border-fun-coral rounded-clay flex items-center gap-2">
            <span className="text-xl" role="img" aria-hidden="true">😅</span>
            <span className="text-rose-700 font-medium">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="spinner-fun-lg mx-auto mb-4"></div>
              <p className="font-display text-lg text-gray-600">Loading topics...</p>
            </div>
          </div>
        ) : topics.length === 0 ? (
          /* Empty State */
          <div className="card-clay-static p-12 text-center">
            <div className="text-6xl mb-4 animate-bounce-gentle">📊</div>
            <h2 className="font-display text-2xl font-bold text-gray-800 mb-2">No topics found</h2>
            <p className="text-gray-600 max-w-md mx-auto">
              Topics are automatically generated when you have unread articles. Check back later or visit your dashboard to see if new topics have been created.
            </p>
          </div>
        ) : (
          /* Topics Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topics.map((topic, index) => (
              <div
                key={topic.id}
                onClick={() => handleTopicClick(topic.id)}
                className="card-clay p-6 cursor-pointer border-l-4 border-primary-500
                  hover:border-l-accent-500"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-display text-lg font-semibold text-gray-800 leading-6 pr-2">
                    {topic.label}
                  </h3>
                  <div className={`px-3 py-1 rounded-clay text-sm font-bold border-2 ${getScoreColor(topic.final_score)}`}>
                    {formatScore(topic.final_score)}%
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <span className="badge-fun-sky text-xs px-2 py-0.5">{topic.unread_count} unread</span>
                  </div>
                  <div className="flex items-center">
                    <span className="badge-fun-mint text-xs px-2 py-0.5">{topic.cluster_size} total</span>
                  </div>
                </div>

                <div className="text-sm text-gray-500 space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Average age:</span>
                    <span className="font-medium">{formatRecency(topic.avg_recency_days)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Created:</span>
                    <span className="font-medium">{formatDate(topic.created_at)}</span>
                  </div>
                  {topic.avg_feedback_score !== 0 && (
                    <div className="flex items-center justify-between">
                      <span>Avg feedback:</span>
                      <span className="font-medium">
                        {topic.avg_feedback_score > 0 ? '👍' : '👎'}
                        {Math.abs(topic.avg_feedback_score).toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t-2 border-clay-border">
                  <div className="text-xs text-gray-400 flex items-center justify-between">
                    <span className="badge-fun-lavender text-xs px-2 py-0.5">{topic.algorithm}</span>
                    {topic.topic_metadata?.silhouette_score && (
                      <span>Quality: {(topic.topic_metadata.silhouette_score * 100).toFixed(1)}%</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Topics;